import React, { useContext, useEffect, useMemo } from "react";

import { fireTrack } from "@swa-ui/analytics";
import { AnalyticsDataUpdater, ApplicationContext } from "@swa-ui/application";
import { usePrevious } from "@swa-ui/application";
import { AuthContext, useUserInfo } from "@swa-ui/authentication";
import { getBootstrapData } from "@swa-ui/bootstrap";
import { useDeviceInfo } from "@swa-ui/browser";
import { ChaseContext } from "@swa-ui/chase";
import { useGeolocation } from "@swa-ui/geolocation";
import { usePersonas } from "@swa-ui/personas";

import { LocaleContext } from "../LocaleProvider";

export const DotcomAnalyticsDataUpdater = () => {
  const { chaseDetails } = useContext(ChaseContext);
  const { isLoggedIn } = useContext(AuthContext);
  const { locale } = useContext(LocaleContext);
  const { site } = useContext(ApplicationContext);

  const { acquisitionSourceCodes, highValueIndicator, offerIdentifier } = chaseDetails ?? {};
  const { getCorporateInfo, getCustomerInfo, rawUserInfoData: userInfo } = useUserInfo();
  const { isSecureWebView } = useDeviceInfo();
  const { accountNumber, redeemablePoints, tier } = getCustomerInfo() ?? {};
  const { companyId, role } = getCorporateInfo() ?? {};
  const { isCorporatePersona } = usePersonas();
  const geolocation = getBootstrapData("air/stations-data") && useGeolocation();
  const nearestStation = geolocation?.nearestStation;
  const loggedIn = isLoggedIn();
  const lastLoggedIn = usePrevious(loggedIn);
  const additionalAnalyticsData = useMemo(
    () => getAnalyticsDataUpdaterProps(),
    [accountNumber, nearestStation]
  );
  const chaseAnalyticsData = useMemo(() => getChaseAnalyticsDataProps(), [chaseDetails]);
  const eventSpecificAnalyticsData = useMemo(() => getAnalyticsDataEventProps(), [loggedIn]);

  useEffect(() => {
    if (lastLoggedIn !== undefined && loggedIn) {
      fireTrack("globalLogIn");
    } else if (lastLoggedIn && !loggedIn) {
      fireTrack("globalLogOut");
    }
  }, [loggedIn]);

  return (
    <AnalyticsDataUpdater
      additionalAnalyticsData={additionalAnalyticsData}
      eventSpecificAnalyticsData={eventSpecificAnalyticsData}
      chaseAnalyticsData={chaseAnalyticsData}
    />
  );

  function getAnalyticsDataUpdaterProps() {
    return {
      iswebview: isSecureWebView ? "1" : "0",
      member_number: accountNumber ? accountNumber : "",
      member_pointsbalance: accountNumber ? redeemablePoints : "",
      member_status: accountNumber ? tier?.toLowerCase() : "",
      page_language: locale,
      session: getSessionData(),
      user_loginstate: accountNumber ? "hot" : "cold",
      ...(isCorporatePersona && {
        swabiz_companyid: companyId ? `${companyId}` : "",
        swabiz_userrole: role ? role : "",
      }),
      ...(nearestStation && { nearestStation }),
    };
  }

  function getAnalyticsDataEventProps() {
    const analyticsEventSpecificObjectKey = loggedIn ? "member_login" : "member_logout";

    return lastLoggedIn !== undefined
      ? {
          [analyticsEventSpecificObjectKey]: "1",
        }
      : {};
  }

  function getChaseAnalyticsDataProps() {
    return {
      chase_acqsrccode: acquisitionSourceCodes ? acquisitionSourceCodes : "",
      chase_highvalueindicator: highValueIndicator ? "1" : "0",
      chase_offeridentifier: offerIdentifier ? offerIdentifier : "",
    };
  }

  function getSessionData() {
    return {
      authenticated: loggedIn,
      ...(loggedIn &&
        userInfo &&
        site === "corporate" && {
          corporate: {
            companyId: userInfo["corporate.companyUserInformation.companyId"],
            name: userInfo["corporate.companyUserInformation.name"],
            role: userInfo["corporate.corporateUserInformation.role"],
            travelPolicies: {
              approvedCars:
                userInfo["corporate.companyUserInformation.travelPolicies.approvedCars"],
              carOption: userInfo["corporate.companyUserInformation.travelPolicies.carOption"],
              carOptionAvailable:
                userInfo["corporate.companyUserInformation.travelPolicies.carOptionAvailable"],
              hotelOptionAvailable:
                userInfo["corporate.companyUserInformation.travelPolicies.hotelOptionAvailable"],
            },
          },
        }),
      ...(loggedIn &&
        userInfo && {
          customer: {
            accountCreatedDate: userInfo["customers.UserInformation.accountCreatedDate"],
            accountNumber: userInfo["customers.UserInformation.accountNumber"],
            accountStatus: userInfo["customers.UserInformation.accountStatus"],
            accountType: userInfo["customers.UserInformation.accountType"],
            chaseVisaRrEnrolled: userInfo["customers.UserInformation.chaseVisaRrEnrolled"],
            companionPassInfo: {
              companionDeclared:
                userInfo["customers.UserInformation.companionPassInfo.companionDeclared"],
              companionPassAchieved:
                userInfo["customers.UserInformation.companionPassInfo.companionPassAchieved"],
              companionQualifyingPoints:
                userInfo["customers.UserInformation.companionPassInfo.companionQualifyingPoints"],
              companionQualifyingPointsRemaining:
                userInfo[
                  "customers.UserInformation.companionPassInfo.companionQualifyingPointsRemaining"
                ],
              companionQualifyingPointsRequired:
                userInfo[
                  "customers.UserInformation.companionPassInfo.companionQualifyingPointsRequired"
                ],
            },
            firstName: userInfo["customers.UserInformation.firstName"],
            lastName: userInfo["customers.UserInformation.lastName"],
            memberStartDate: userInfo["customers.UserInformation.memberStartDate"],
            primaryEmail: userInfo["customers.UserInformation.primaryEmail"],
            primaryPhoneNumber: userInfo["customers.UserInformation.primaryPhoneNumber"],
            redeemablePoints: userInfo["customers.UserInformation.redeemablePoints"],
            tier: userInfo["customers.UserInformation.tier"],
            tierStatusPending: userInfo["customers.UserInformation.tierStatusPending"],
          },
        }),
    };
  }
};
DotcomAnalyticsDataUpdater.displayName = "DotcomAnalyticsDataUpdater";
